import { Injectable } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { Table } from '@cureskin/api-client';
import { AppConfig } from '../../app/app.config';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ChatMessage,
  ChatState,
  PaginationState,
  PollingConfig,
  ChatQueryParams,
  FetchChatOptions,
} from './chat-service.types';
import {TreatmentSession} from '@cureskin/api-client/src/server-api';

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private static readonly DEFAULT_PAGE_SIZE = 100;
  private static readonly DEFAULT_POLLING_CONFIG: PollingConfig = {
    initialInterval: 1000,
    maxInterval: 34000
  };
  private static readonly MAX_RETRY_ATTEMPTS = 3;
  private static readonly RETRY_DELAY = 1000;

  private readonly state = new BehaviorSubject<ChatState>({
    messages: [],
    isThinking: false,
    latestMessage: null,
    isLoadingOldMessages: false
  });

  private readonly pagination: PaginationState = {
    currentSkip: 0,
    hasMore: true,
    pageSize: ChatService.DEFAULT_PAGE_SIZE
  };

  private pollingTimer: NodeJS.Timeout | null = null;
  private pollingIntervals = {
    interval1: ChatService.DEFAULT_POLLING_CONFIG.initialInterval,
    interval2: ChatService.DEFAULT_POLLING_CONFIG.initialInterval
  };
  private lastMessageTimestamp: string | null = null;

  constructor(
    private readonly connectionService: ConnectionService,
    private readonly appConfig: AppConfig,
  ) {}

  get showThinking$(): Observable<boolean> {
    return new Observable(subscriber =>
      this.state.subscribe(state => subscriber.next(state.isThinking))
    );
  }

  get allChats$(): Observable<ChatMessage[]> {
    return new Observable(subscriber =>
      this.state.subscribe(state => subscriber.next(state.messages))
    );
  }

  get latestMessage$(): Observable<ChatMessage | null> {
    return new Observable(subscriber =>
      this.state.subscribe(state => subscriber.next(state.latestMessage))
    );
  }

  get loadingOldMessages$(): Observable<boolean> {
    return new Observable(subscriber =>
      this.state.subscribe(state => subscriber.next(state.isLoadingOldMessages))
    );
  }

  setThinking(value: boolean): void {
    this.updateState({ isThinking: value });
  }

  async loadTreatmentChatHistory(params: ChatQueryParams, reset: boolean = true): Promise<ChatMessage[]> {
    if (reset) {
      this.resetPaginationState();
    }

    const chats = await this.fetchChatMessages({
      ...params,
      limit: this.pagination.pageSize,
      skip: reset ? 0 : this.pagination.currentSkip
    });

    const messages = this.processNewMessages(chats, reset);
    this.updateMessagesState(messages);

    return messages;
  }

  async loadMoreMessages(params: ChatQueryParams): Promise<boolean> {
    if (!this.pagination.hasMore || this.state.value.isLoadingOldMessages) {
      return false;
    }

    this.updateState({ isLoadingOldMessages: true });

    try {
      this.pagination.currentSkip += this.pagination.pageSize;
      await this.loadTreatmentChatHistory(params, false);
      return this.pagination.hasMore;
    } finally {
      this.updateState({ isLoadingOldMessages: false });
    }
  }

  private updateState(partialState: Partial<ChatState>): void {
    this.state.next({
      ...this.state.value,
      ...partialState
    });
  }

  private resetPaginationState(): void {
    this.pagination.currentSkip = 0;
    this.pagination.hasMore = true;
    this.updateState({ messages: [] });
  }

  private async fetchChatMessages(options: FetchChatOptions): Promise<any[]> {
    const response = await this.connectionService.fetchTreatmentChat({
      where: {
        user: options.user,
        treatmentSession: options.treatmentSession,
        ...(options.createdAfter && { createdAt: { $gt: options.createdAfter } })
      },
      descending: 'createdAt',
      limit: options.limit ?? this.pagination.pageSize,
      skip: options.skip ?? 0,
      option: { context: { translate: true } }
    });

    this.pagination.hasMore = response.length >= (options.limit ?? this.pagination.pageSize);
    return response;
  }

  private processNewMessages(chats: any[], reset: boolean): ChatMessage[] {
    if (reset) {
        return this.processResetMessages(chats);
    }
    return this.processPaginatedMessages(chats);
  }

  private processResetMessages(chats: any[]): ChatMessage[] {
    return [...chats].reverse();
  }

  private processPaginatedMessages(chats: any[]): ChatMessage[] {
    const reversedChats = [...chats].reverse();
    const currentMessages = this.state.value.messages;

    return this.isPrependingMessages()
        ? this.prependMessages(reversedChats, currentMessages)
        : this.appendMessages(reversedChats, currentMessages);
  }

  private isPrependingMessages(): boolean {
    return this.pagination.currentSkip > 0;
  }

  private prependMessages(newChats: ChatMessage[], existingChats: ChatMessage[]): ChatMessage[] {
    return [...newChats, ...existingChats];
  }

  private appendMessages(newChats: ChatMessage[], existingChats: ChatMessage[]): ChatMessage[] {
    return [...existingChats, ...newChats];
  }

  private updateMessagesState(messages: ChatMessage[]): void {
    if (messages.length > 0) {
      this.updateState({
        messages,
        latestMessage: messages[messages.length - 1]
      });
    }
  }

  async sendMessage(user: Table.User, treatmentSession: TreatmentSession, lastMessage: any, userReply: string): Promise<any> {
    this.setThinking(true);
    const reply = await this.createReplyMessage(user, treatmentSession, lastMessage, userReply);
    await this.saveAndUpdateMessageState(reply);
    await this.scheduleNextBotFetch(user, treatmentSession);

    return reply;
  }

  private createReplyMessage(user: Table.User, treatmentSession: TreatmentSession, lastMessage: any, userReply: string): any {
    const clsName = lastMessage?.className;
    const reply: any = new Table[clsName]({ user, treatmentSession });

    if (lastMessage?.get('Owner') !== 'USER' && lastMessage?.get('BotMetadata')) {
        reply.set('BotMetadata', `REPLY_${lastMessage.get('BotMetadata')}`);
    }

    reply.set('Mode', this.determineMessageMode(lastMessage));

    this.setMessageContext(reply, lastMessage);

    reply.set('Message', userReply);
    reply.set('Owner', this.appConfig.Shared.Assistant.Owner.USER);
    reply.set('UserId', user.get('username'));
    reply.set('uniqueId', lastMessage?.id);

    return reply;
  }

  private determineMessageMode(lastMessage: any): string {
    const defaultMode = this.appConfig.Shared.Assistant.Mode.TEXT_NOREPLY;
    if (!lastMessage) return defaultMode;

    const responseModes = this.appConfig.Shared.Assistant.ResponseModes;
    return responseModes.includes(lastMessage.get('Mode'))
        ? `${lastMessage.get('Mode')}_RESPONSE`
        : defaultMode;
  }

  private setMessageContext(reply: any, lastMessage: any): void {
    if (!lastMessage) return;

    reply.set('questionId', lastMessage.get('questionId'));
    reply.set('context', lastMessage.get('context'));
    reply.set('messageAttendedByBot', lastMessage.get('messageAttendedByBot'));
  }

  private async saveAndUpdateMessageState(reply: any): Promise<void> {
    await reply.save();

    const currentChats = this.state.value.messages;
    const updatedChats = [...currentChats, reply];

    this.updateState({
        messages: updatedChats,
        latestMessage: reply
    });

    this.lastMessageTimestamp = reply.get('createdAt');
  }

  private async scheduleNextBotFetch(user: Table.User, treatmentSession: TreatmentSession): Promise<void> {
    this.stopBotReplyTimer();
    const wait = this.calculateNextPollingInterval();

    this.pollingTimer = setTimeout(
        () => this.executeBotFetch(user, treatmentSession),
        wait
    );
  }

  private async executeBotFetch(user: Table.User, treatmentSession: TreatmentSession, retryCount = 0): Promise<void> {
    try {
        const newMessages = await this.fetchNewMessages(user, treatmentSession);

        if (newMessages.length > 0) {
            await this.handleNewBotMessages(newMessages);
            return;
        }

        this.updatePollingIntervals();
        await this.scheduleNextBotFetch(user, treatmentSession);
    } catch (error) {
        if (retryCount < ChatService.MAX_RETRY_ATTEMPTS) {
            setTimeout(
                () => this.executeBotFetch(user, treatmentSession, retryCount + 1),
                ChatService.RETRY_DELAY
            );
        } else {
            await this.scheduleNextBotFetch(user, treatmentSession);
        }
    }
  }

  private async fetchNewMessages(user: Table.User, treatmentSession: TreatmentSession): Promise<any[]> {
    return this.connectionService.fetchTreatmentChat({
        where: {
            user,
            treatmentSession,
            createdAt: { $gt: this.lastMessageTimestamp },
        },
        descending: 'createdAt',
        limit: 10,
        option: { context: { translate: true } }
    });
  }

  private async handleNewBotMessages(newMessages: any[]): Promise<void> {
    this.stopBotReplyTimer();
    this.setThinking(false);

    const currentChats = this.state.value.messages;
    const updatedChats = [...currentChats, ...newMessages.reverse()];

    this.updateState({
        messages: updatedChats,
        latestMessage: updatedChats[updatedChats.length - 1]
    });
  }

  private calculateNextPollingInterval(): number {
    return this.pollingIntervals.interval1 + this.pollingIntervals.interval2;
  }

  private updatePollingIntervals(): void {
    const { interval1, interval2 }: { interval1: number, interval2: number } = this.pollingIntervals;
    this.pollingIntervals = {
        interval1: interval2,
        interval2: interval1 + interval2
    };
  }

  addLocalMessages(messages: any[]): void {
    const currentMessages = this.state.value.messages;
    const updatedMessages = [...currentMessages, ...messages];
    this.updateState({
      messages: updatedMessages,
      latestMessage: updatedMessages[updatedMessages.length - 1]
    });
  }

  replaceMessages(messages: any[]): void {
    this.updateState({
      messages: messages as ChatMessage[],
      latestMessage: messages.length > 0 ? messages[messages.length - 1] as ChatMessage : null
    });
  }

  stopBotReplyTimer() {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
      this.pollingIntervals = {
        interval1: ChatService.DEFAULT_POLLING_CONFIG.initialInterval,
        interval2: ChatService.DEFAULT_POLLING_CONFIG.initialInterval
      };
    }
  }
}
